<template>
  <div class="gmf-fullbody-leaderboard-section gmf-context">
    <template v-if="selectedLeaderboard">
      <div class="gmf-fullbody-leaderboard-container">
        <div v-for="(row, index) in leaderboardRows"
          class="gmf-fullbody-leaderboard-item"
          :style="{ order: index }"
          :key="row.customerId">
          <Avatar view="fullbody" animated disableOnlineIndicator :customer="customerObject(row)" />
        </div>
        <div class="gmf-fullbody-leaderboard-list" style="order: 0;">
          <div v-for="row in leaderboardRows" :key="row.customerId" class="gmf-fullbody-leaderboard-item">
            <img class="gmf-fullbody-leaderboard-icon" v-if="row.rank == 1" src="https://cdn.gamifiera.com/static/icons/trophy_gold.svg" />
            <img class="gmf-fullbody-leaderboard-icon" v-if="row.rank == 2" src="https://cdn.gamifiera.com/static/icons/trophy_silver.svg" />
            <img class="gmf-fullbody-leaderboard-icon" v-if="row.rank == 3" src="https://cdn.gamifiera.com/static/icons/trophy_bronze.svg" />
            <div class="gmf-fullbody-leaderboard-customer-name">
            <CustomerName class="gmf-fullbody-leaderboard-customer-name gmf-subheader" :customer="{
                id: row.customerId,
                publicProfile: true,
                isGuest: false,
                firstName: row.customerName,
              }" />
              <div class="gmf-fullbody-leaderboard-score">{{ niceThousands(row.score) }} {{ translatedBoardName }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style>
.gmf-fullbody-leaderboard-section {
  width: 100%;
  display: flex;
}
.gmf-fullbody-leaderboard-container {
  width: 100%;
  display: flex;
  margin-top: 0px;
  border-spacing: 0;
  justify-content: center;
  color: var(--gmf-col-black);
}
.gmf-fullbody-leaderboard-list {
  z-index: 1;
  display: flex;
  gap: var(--gmf-p-3);
  flex-direction: column;
  justify-content: center;
  margin-inline: var(--gmf-p-2);
}
.gmf-fullbody-leaderboard-item {
  display: flex;
  gap: var(--gmf-p-3);
  align-items: center;
  flex-direction: row;
}
.gmf-fullbody-leaderboard .gmf-avatar {
  width: 80px;
  height: 160px;
  max-width: unset;
  max-height: unset;
  background: none;
}
.gmf-fullbody-leaderboard-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
}
.gmf-fullbody-leaderboard-customer-name.gmf-subheader {
  flex-shrink: 0;
  margin-bottom: 0;
}
.gmf-fullbody-leaderboard .gmf-fullbody-leaderboard-score {
  flex-shrink: 0;
  white-space: nowrap;
  margin-top: var(--gmf-p-1);
  color: var(--gmf-brand-color);
  font-size: var(--gmf-text-small-font-size);
}
.gmf-fullbody-leaderboard .gmf-avatar-popout.gmf-avatar-3d-fullbody .gmf-avatar-content {
  top: -50%;
  left: -125%;
  right: -125%;
  height: 175%;
}
.gmf-fullbody-leaderboard .gmf-avatar-content {
  pointer-events: none;
}
</style>

<script>
import state from '@/state';
import callApi from '@/utils/callApi';
import Avatar from '@/components/Avatar.vue';
import CustomerName from '@/components/CustomerName';

const t = state.t.prefix('leaderboards');

export default {
  inject: ['injectedLeaderboard'],
  components: {
    Avatar,
    CustomerName,
  },
  data () {
    return {
      state,

      page: 1,
      itemsPerPage: 3,
      leaderboardRows: [],

      leaderboards: [],
      selectedPeriod: null,
      selectedLeaderboard: null,

      isLoading: false,
    };
  },

  computed: {
    translatedBoardName() {
      return this.t(`boards.${this.selectedLeaderboard.name}`).toLowerCase()
    }
  },

  async mounted () {
    if (this.injectedLeaderboard) {
      let injectedData = this.injectedLeaderboard.split(':');
      this.selectedLeaderboard = { name: injectedData[0] };
      this.selectedPeriod = injectedData[1]; // Will trigger load
      return;
    }

    const listRes = await callApi('GET', '/v1/client/leaderboards');
    const { leaderboards } = await listRes.json();

    this.leaderboards = leaderboards;
    if (leaderboards.length > 0) {
      this.selectedLeaderboard = leaderboards[0];
      this.selectedPeriod = this.selectedLeaderboard.periods[0]; // Will trigger load
    }
  },
  methods: {
    t,
    niceThousands (value) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    reset () {
      this.isLoading = false;
      this.leaderboardRows = [];
      this.page = 1;
    },
    async load () {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      const res = await callApi('GET', '/v1/client/leaderboards/' + this.selectedLeaderboard.name + '?period=' + this.selectedPeriod + '&take=3');
      const { rows, error } = await res.json();

      if (error) {
        switch (error.code) {
          case 804003:
            console.error("Failed to load leaderboard in gmf-fullbody-leaderboard, leaderboard does not exist:", this.selectedLeaderboard.name);
            break;
          case 804004:
            console.error("Failed to load leaderboard in gmf-fullbody-leaderboard, invalid time period:", this.selectedPeriod);
            break;
          default:
            console.error("Failed to load leaderboard in gmf-fullbody-leaderboard.");
        }
        this.isLoading = false;
        return;
      }

      this.leaderboardRows.push(...rows);

      this.page++;

      this.isLoading = false;
    },
    customerObject (row) {
      return {
        id: row.customerId,
        name: row.customerName,
        level: row.customerLevel,
        avatarCode: row.avatarCode,
        levelName: row.customerLevelName,

        publicProfile: true,
        isOnline: row.isOnline,
        lastOnline: row.lastOnline,
      }
    },
  },
  watch: {
    selectedPeriod () {
      this.reset();
      this.load();
    },
  },
};
</script>
